import { motion } from "framer-motion";
import { Building2, Users, TrendingUp, Clock, CheckCircle, ArrowRight, Zap, Shield, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IMAGES } from "@/assets/images";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const advantages = [
  {
    icon: Zap,
    title: "高效招聘",
    description: "平均3天内收到合格简历，比传统招聘渠道快70%"
  },
  {
    icon: Target,
    title: "精准匹配",
    description: "AI智能推荐系统，为您筛选最匹配的候选人"
  },
  {
    icon: Shield,
    title: "质量保证",
    description: "严格的人才审核机制，确保候选人质量"
  },
  {
    icon: Users,
    title: "海量人才",
    description: "超过50万+活跃求职者，覆盖各行各业"
  }
];

const steps = [
  {
    number: "01",
    title: "注册账号",
    description: "快速注册企业账号，完善公司信息"
  },
  {
    number: "02",
    title: "发布职位",
    description: "填写职位详情，设置招聘要求"
  },
  {
    number: "03",
    title: "筛选简历",
    description: "查看应聘者简历，邀请面试"
  },
  {
    number: "04",
    title: "成功招聘",
    description: "完成招聘流程，找到理想人才"
  }
];

const pricingPlans = [
  {
    name: "基础版",
    price: "免费",
    period: "",
    features: [
      "发布3个职位",
      "基础简历筛选",
      "标准客服支持",
      "30天职位有效期"
    ],
    highlighted: false
  },
  {
    name: "专业版",
    price: "¥2,999",
    period: "/月",
    features: [
      "发布20个职位",
      "AI智能推荐",
      "优先客服支持",
      "60天职位有效期",
      "数据分析报告",
      "品牌展示位"
    ],
    highlighted: true
  },
  {
    name: "企业版",
    price: "¥9,999",
    period: "/月",
    features: [
      "无限职位发布",
      "专属招聘顾问",
      "7x24小时支持",
      "90天职位有效期",
      "深度数据分析",
      "首页推荐位",
      "定制化服务"
    ],
    highlighted: false
  }
];

const stats = [
  { value: "50万+", label: "活跃求职者" },
  { value: "10万+", label: "合作企业" },
  { value: "85%", label: "招聘成功率" },
  { value: "3天", label: "平均招聘周期" }
];

const testimonials = [
  {
    company: "字节跳动",
    logo: IMAGES.COMPANY_BG_1,
    quote: "通过该平台，我们在一周内成功招聘到5名优秀的前端工程师，效率远超预期。",
    author: "HR总监 张女士"
  },
  {
    company: "阿里巴巴",
    logo: IMAGES.COMPANY_BG_2,
    quote: "平台的AI推荐系统非常精准，为我们节省了大量筛选简历的时间。",
    author: "招聘经理 李先生"
  },
  {
    company: "腾讯",
    logo: IMAGES.COMPANY_BG_3,
    quote: "专业的服务团队和完善的招聘流程，让我们的招聘工作变得更加高效。",
    author: "人力资源部 王女士"
  }
];

export default function ForEmployers() {
  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.TEAM_WORK_2}
            alt="团队协作"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              为企业提供专业招聘服务
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              连接优秀人才与卓越企业，让招聘更高效、更精准
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8">
                立即发布职位
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8">
                了解更多
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold mb-4">为什么选择我们</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              专业的招聘平台，为企业提供全方位的人才招聘解决方案
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <motion.div key={index} variants={fadeInUp}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{advantage.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{advantage.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold mb-4">简单四步，开启招聘</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              快速上手，轻松发布职位，高效招聘人才
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                variants={fadeInUp}
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-primary-foreground text-2xl font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-0.5 bg-border" />
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold mb-4">灵活的定价方案</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              选择适合您企业的方案，开启高效招聘之旅
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {pricingPlans.map((plan, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className={`h-full ${plan.highlighted ? 'border-primary shadow-lg scale-105' : ''}`}>
                  <CardHeader>
                    {plan.highlighted && (
                      <div className="inline-block px-3 py-1 rounded-full bg-primary text-primary-foreground text-sm font-medium mb-4">
                        推荐
                      </div>
                    )}
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription className="text-3xl font-bold text-foreground mt-4">
                      {plan.price}
                      <span className="text-base font-normal text-muted-foreground">{plan.period}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full mt-6"
                      variant={plan.highlighted ? 'default' : 'outline'}
                    >
                      选择方案
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold mb-4">客户成功案例</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              听听我们的合作伙伴怎么说
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                        <img
                          src={testimonial.logo}
                          alt={testimonial.company}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{testimonial.company}</CardTitle>
                        <CardDescription>{testimonial.author}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground italic">"{testimonial.quote}"</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.TEAM_WORK_3}
            alt="团队合作"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/60" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              准备好开始招聘了吗？
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              立即注册，发布您的第一个职位，找到理想的候选人
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8">
                免费注册
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8">
                联系我们
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}